import { httpClient } from '@/shared/api/httpClient'
import type { ConversionStatusResponse, OrderStatusResponse } from '../types/payment'
import type { GonkaRecoveryState } from '../types/recovery'
import type { AminoSignDoc, KeeperSignature } from '../services/keeperPayment'

const conversionId = (value: unknown): value is string => typeof value === 'string' && /^[a-zA-Z0-9_-]{1,64}$/.test(value)
const gnkAmount = (value: unknown): value is string => typeof value === 'string' && /^\d{1,78}(?:\.\d{1,9})?$/.test(value)
function conversionRecord(value: unknown): Record<string, unknown> {
    if (!value || typeof value !== 'object' || Array.isArray(value)) throw new Error('CONVERSION_INVALID_RESPONSE')
    return value as Record<string, unknown>
}
function conversionData(response: unknown): Record<string, unknown> {
    const envelope = conversionRecord(response)
    if (envelope.result !== undefined && envelope.result !== 'success') throw new Error('CONVERSION_INVALID_RESPONSE')
    return conversionRecord(envelope.data)
}
function parseConversion(value: unknown, orderId?: string): ConversionStatusResponse {
    const item = conversionRecord(value)
    const phases: Record<string, string> = { awaiting_payment: 'awaiting_payment', converting: 'converting', credited: 'credited', delayed: 'delayed', manual_review: 'delayed', exchanged: 'credited', partially_purchased: 'credited', prepaid: 'credited', activating: 'credited', activated: 'activated' }
    const fail = () => { throw new Error('CONVERSION_INVALID_RESPONSE') }
    if (typeof item.status !== 'string' || !Object.hasOwn(phases, item.status) || item.phase !== phases[item.status] ||
        !gnkAmount(item.gnk_target) || !/[1-9]/.test(item.gnk_target) || !gnkAmount(item.gnk_credited) ||
        typeof item.purchase_complete !== 'boolean' || !Array.isArray(item.flows) ||
        (item.notification_dismissed !== undefined && typeof item.notification_dismissed !== 'boolean') ||
        ![null, 'processing_delayed', 'manual_review', 'evidence_mismatch', 'basket_insufficient_gnk', 'basket_unavailable'].includes(item.reason as string | null) ||
        ['attempt_id', 'invoice_uuid', 'types', 'actual_gnk_amount'].some(key => key in item) ||
        (item.order_id !== undefined && (!conversionId(item.order_id) || (orderId !== undefined && item.order_id !== orderId)))) fail()
    const flows = item.flows as unknown[]
    const purchase = ['exchanged', 'partially_purchased', 'prepaid', 'activating', 'activated'].includes(String(item.status))
    if (!purchase) {
        if (flows.length || item.purchase_complete) fail()
    } else {
        if (!flows.length || !/[1-9]/.test(String(item.gnk_credited))) fail()
        const types = new Set<string>()
        let bought = 0
        let stopped = false
        const statuses: unknown[] = []
        let previous = ''
        for (const value of flows) {
            const flow = conversionRecord(value)
            if (typeof flow.type !== 'string' || !/^gonka-game_f[1-9][0-9]*$/.test(flow.type) || types.has(flow.type) ||
                (previous && (flow.type.length < previous.length || (flow.type.length === previous.length && flow.type <= previous))) ||
                (flow.price_gnk !== null && !gnkAmount(flow.price_gnk))) fail()
            const type = flow.type as string
            types.add(type); previous = type
            if (flow.purchase_status === 'purchased') {
                if (stopped || !gnkAmount(flow.price_gnk) || !/[1-9]/.test(String(flow.price_gnk)) || !['prepaid', 'activating', 'activated'].some(status => status === flow.status)) fail()
                bought++
            } else {
                if (!['insufficient', 'not_attempted'].some(status => status === flow.purchase_status) || flow.status !== flow.purchase_status ||
                    (stopped && flow.purchase_status !== 'not_attempted') ||
                    (flow.purchase_status === 'insufficient' && (!gnkAmount(flow.price_gnk) || !/[1-9]/.test(String(flow.price_gnk))))) fail()
                stopped = true
            }
            statuses.push(flow.status)
        }
        const complete = bought === flows.length
        const status = !bought ? 'exchanged' : !complete ? 'partially_purchased' : statuses.every(s => s === 'prepaid') ? 'prepaid' : statuses.every(s => s === 'activated') ? 'activated' : 'activating'
        if (item.status !== status || item.purchase_complete !== complete) fail()
    }
    return item as unknown as ConversionStatusResponse
}

export interface KeeperPaymentResponse {
    paymentId: string
    chainId: string
    signDoc: AminoSignDoc | null
    status: 'prepared' | 'submitted' | 'confirmed' | 'failed' | 'unknown'
    txHash: string | null
    code: number | null
    rawLog: string
}

export const PaymentApi = {
    getRecovery: async (orderId: string): Promise<GonkaRecoveryState> => {
        const response = await httpClient.get<{ data: GonkaRecoveryState }>(
            `/payment/gonka-recovery/status?orderId=${encodeURIComponent(orderId)}`, { signal: AbortSignal.timeout(15000) }
        )
        return response.data
    },
    submitRecovery: async (orderId: string, txHash: string): Promise<GonkaRecoveryState> => {
        const response = await httpClient.post<{ data: GonkaRecoveryState }>(
            '/payment/gonka-recovery/submit', { orderId, txHash }, { signal: AbortSignal.timeout(15000), retry: 0 }
        )
        return response.data
    },
    submitRecoveryProof: async (orderId: string, challengeId: string, proof: { txHash: string } | { signature: KeeperSignature['signature'] }): Promise<GonkaRecoveryState> => {
        const response = await httpClient.post<{ data: GonkaRecoveryState }>(
            `/payment/gonka-recovery/${'txHash' in proof ? 'micro' : 'signature'}`,
            { orderId, challengeId, ...proof }, { signal: AbortSignal.timeout(15000), retry: 0 }
        )
        return response.data
    },
    prepareKeeperPayment: async (orderId: string, sender: string): Promise<KeeperPaymentResponse> => {
        const response = await httpClient.post<{ data: KeeperPaymentResponse }>(
            '/payment/gonka-keeper/prepare', { orderId, sender }, { signal: AbortSignal.timeout(30000) }
        )
        return response.data
    },
    broadcastKeeperPayment: async (paymentId: string, result: KeeperSignature): Promise<KeeperPaymentResponse> => {
        const response = await httpClient.post<{ data: KeeperPaymentResponse }>(
            '/payment/gonka-keeper/broadcast', { paymentId, signed: result.signed, signature: result.signature },
            { signal: AbortSignal.timeout(30000), retry: 0 }
        )
        return response.data
    },
    getKeeperPayment: async (orderId: string): Promise<KeeperPaymentResponse | null> => {
        const response = await httpClient.get<{ data: { payment: KeeperPaymentResponse | null } }>(
            `/payment/gonka-keeper/status?orderId=${encodeURIComponent(orderId)}`, { signal: AbortSignal.timeout(15000) }
        )
        return response.data.payment
    },
    /**
     * Poll order status from Redis (no external API calls on backend).
     * Provider-agnostic: works for onchainpay and gonka orders alike.
     * GET /api/payment/order/status?order_id={orderId}
     */
    getOrderStatus: async (orderId: string): Promise<OrderStatusResponse> => {
        const response = await httpClient.get<{ data: OrderStatusResponse }>(
            `/payment/order/status?order_id=${encodeURIComponent(orderId)}`
        )
        return response.data
    },

    /**
     * Poll the USDT→GNK conversion that runs after the invoice is paid.
     * Owner-scoped; answers 404 when the order carries no conversion.
     * GET /api/wallet/conversion/status?order_id={orderId}
     */
    getActiveConversions: async (cursor?: string, signal?: AbortSignal): Promise<{ items: (ConversionStatusResponse & { order_id: string })[]; next_cursor: string | null }> => {
        const response = await httpClient.get<{ data: { items: (ConversionStatusResponse & { order_id: string })[]; next_cursor: string | null } }>(
            `/wallet/conversion/active?limit=20${cursor ? `&cursor=${encodeURIComponent(cursor)}` : ''}`, { signal }
        )
        const page = conversionData(response)
        if (!Array.isArray(page.items) || !(page.next_cursor === null || conversionId(page.next_cursor))) throw new Error('CONVERSION_INVALID_RESPONSE')
        const items = page.items.map(value => {
            const item = conversionRecord(value)
            if (!conversionId(item.order_id)) throw new Error('CONVERSION_INVALID_RESPONSE')
            return { ...parseConversion(item), order_id: item.order_id }
        })
        if (new Set(items.map(item => item.order_id)).size !== items.length) throw new Error('CONVERSION_INVALID_RESPONSE')
        return { items, next_cursor: page.next_cursor }
    },
    dismissConversion: async (orderId: string, signal?: AbortSignal): Promise<void> => {
        const response = await httpClient.post<{ data: { notification_dismissed: boolean } }>(
            '/wallet/conversion/dismiss', { order_id: orderId }, { signal }
        )
        if (conversionData(response).notification_dismissed !== true) throw new Error('CONVERSION_INVALID_RESPONSE')
    },
    getConversionStatus: async (orderId: string, signal?: AbortSignal): Promise<ConversionStatusResponse> => {
        const response = await httpClient.get<{ data: ConversionStatusResponse }>(
            `/wallet/conversion/status?order_id=${encodeURIComponent(orderId)}`, { signal }
        )
        return parseConversion(conversionData(response), orderId)
    },
}
